import { type FormEvent, useMemo, useState } from 'react';
import { ArrowUpDown, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { useCreateEventDeliveryAddressMutation } from '@/graphql/mutations/createEventDeliveryAddress.generated';
import { useDeleteEventDeliveryAddressMutation } from '@/graphql/mutations/deleteEventDeliveryAddress.generated';
import { useUpdateEventDeliveryAddressMutation } from '@/graphql/mutations/updateEventDeliveryAddress.generated';
import { useGetDeliveryAddressesForEventQuery } from '@/graphql/queries/getDeliveryAddressesForEvent.generated';
import { toast } from 'sonner';
import { Spinner } from '@/components/ui/spinner';
import i18n from '@/utils/i18n';
import { Separator } from '@/components/ui/separator';

type SortKey = 'name' | 'linked_item_location_count';
type SortDirection = 'asc' | 'desc';

function getErrorMessage(error: unknown, fallbackMessage: string) {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallbackMessage;
}

export function EventDeliveryAddressesTab({ eventId }: { eventId: string }) {
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newStreetname, setNewStreetname] = useState('');
  const [newStreetno, setNewStreetno] = useState('');
  const [newAdditionalInfo, setNewAdditionalInfo] = useState('');
  const [newIsEventDefault, setNewIsEventDefault] = useState(false);
  const [deleteConfirmAddress, setDeleteConfirmAddress] = useState<{
    id: string;
    name?: string | null;
    linked_item_location_count: number;
  } | null>(null);
  const [deletingAddressId, setDeletingAddressId] = useState<string | null>(
    null,
  );
  const [promotingAddressId, setPromotingAddressId] = useState<string | null>(
    null,
  );

  const { data, loading, error, refetch } =
    useGetDeliveryAddressesForEventQuery({
      variables: { event_id: eventId },
      fetchPolicy: 'network-only',
    });
  const [createDeliveryAddress, { loading: creatingAddress }] =
    useCreateEventDeliveryAddressMutation();
  const [deleteDeliveryAddress, { loading: deletingAddress }] =
    useDeleteEventDeliveryAddressMutation();
  const [updateDeliveryAddress, { loading: promotingAddress }] =
    useUpdateEventDeliveryAddressMutation();

  const deliveryAddresses = useMemo(() => {
    const items = [...(data?.getDeliveryAddressesForEvent || [])];
    items.sort((a, b) => {
      if (sortKey === 'name') {
        const aName = (a.name || '').toLocaleLowerCase();
        const bName = (b.name || '').toLocaleLowerCase();
        return sortDirection === 'asc'
          ? aName.localeCompare(bName)
          : bName.localeCompare(aName);
      }

      return sortDirection === 'asc'
        ? a.linked_item_location_count - b.linked_item_location_count
        : b.linked_item_location_count - a.linked_item_location_count;
    });
    return items;
  }, [data?.getDeliveryAddressesForEvent, sortDirection, sortKey]);

  // Order links (and the orders placed through them) are delivered to the
  // event default, so colugo refuses to create a link without one.
  const hasDefaultAddress = deliveryAddresses.some((a) => a.is_event_default);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDirection('asc');
  };

  const canSubmitAddress =
    Boolean(newStreetname.trim()) && Boolean(newAdditionalInfo.trim());

  const handleCreateAddress = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSubmitAddress) return;
    const streetname = newStreetname.trim();
    const additionalInfo = newAdditionalInfo.trim();

    try {
      const result = await createDeliveryAddress({
        variables: {
          event_id: eventId,
          input: {
            name: additionalInfo,
            streetname,
            streetno: newStreetno.trim() || undefined,
            additional_info: additionalInfo,
            is_event_default: newIsEventDefault,
          },
        },
      });

      if (result.errors?.length) {
        throw new Error(
          result.errors[0]?.message || i18n.t('Events:failedToCreateAddress'),
        );
      }

      toast.success(i18n.t('Events:deliveryAddressAdded'));
      setIsAddDialogOpen(false);
      setNewStreetname('');
      setNewStreetno('');
      setNewAdditionalInfo('');
      setNewIsEventDefault(false);
      await refetch();
    } catch (mutationError: unknown) {
      toast.error(
        getErrorMessage(
          mutationError,
          i18n.t('Events:errorCreatingDeliveryAddress'),
        ),
      );
    }
  };

  const runPromote = async (addressId: string) => {
    setPromotingAddressId(addressId);
    try {
      const result = await updateDeliveryAddress({
        variables: {
          delivery_address_id: addressId,
          input: { is_event_default: true },
        },
      });

      if (result.errors?.length) {
        throw new Error(
          result.errors[0]?.message ||
            i18n.t('Events:failedToSetDefaultAddress'),
        );
      }

      toast.success(i18n.t('Events:defaultDeliveryAddressUpdated'));
      await refetch();
    } catch (mutationError: unknown) {
      toast.error(
        getErrorMessage(
          mutationError,
          i18n.t('Events:errorSettingDefaultAddress'),
        ),
      );
    } finally {
      setPromotingAddressId(null);
    }
  };

  const runDelete = async (addressId: string) => {
    setDeletingAddressId(addressId);
    try {
      const result = await deleteDeliveryAddress({
        variables: { delivery_address_id: addressId },
      });

      if (result.errors?.length) {
        throw new Error(
          result.errors[0]?.message || i18n.t('Events:failedToDeleteAddress'),
        );
      }

      toast.success(i18n.t('Events:deliveryAddressDeleted'));
      setDeleteConfirmAddress((current) =>
        current?.id === addressId ? null : current,
      );
      await refetch();
    } catch (mutationError: unknown) {
      toast.error(
        getErrorMessage(
          mutationError,
          i18n.t('Events:errorDeletingDeliveryAddress'),
        ),
      );
    } finally {
      setDeletingAddressId(null);
    }
  };

  const handleDeleteClick = (address: {
    id: string;
    name?: string | null;
    is_event_default: boolean;
    linked_item_location_count: number;
  }) => {
    if (address.is_event_default) return;

    if (address.linked_item_location_count > 0) {
      setDeleteConfirmAddress({
        id: address.id,
        name: address.name,
        linked_item_location_count: address.linked_item_location_count,
      });
      return;
    }

    void runDelete(address.id);
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-6">
      <div className="tw:flex tw:items-start tw:justify-between tw:gap-4">
        <div className="tw:space-y-2">
          <h1 className="tw:text-4xl tw:font-extrabold">
            {i18n.t('Events:deliveryAddressesTitle')}
          </h1>
          <p>{i18n.t('Events:deliveryAddressesDescription')}</p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)}>
          <Plus />
          {i18n.t('Events:addDeliveryAddress')}
        </Button>
      </div>

      <Separator />

      {!loading && !error && !hasDefaultAddress && (
        <p className="tw:text-destructive tw:text-sm">
          {i18n.t('Events:noDefaultDeliveryAddressWarning')}
        </p>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="tw:w-1/3">
              <Button
                variant="ghost"
                size="sm"
                className="tw:-ml-2"
                onClick={() => handleSort('name')}
              >
                {i18n.t('Events:deliveryAddressColumn')}
                <ArrowUpDown className="tw:size-4" />
              </Button>
            </TableHead>
            <TableHead className="tw:w-1/3">
              <Button
                variant="ghost"
                size="sm"
                className="tw:-ml-2"
                onClick={() => handleSort('linked_item_location_count')}
              >
                {i18n.t('Events:linkedItemLocationsColumn')}
                <ArrowUpDown className="tw:size-4" />
              </Button>
            </TableHead>
            <TableHead className="tw:w-1/3" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            Array.from({ length: 2 }).map((_, index) => (
              <TableRow key={`loading-${index}`}>
                <TableCell>
                  <Skeleton className="tw:h-4 tw:w-48" />
                </TableCell>
                <TableCell>
                  <Skeleton className="tw:h-5 tw:w-6 tw:rounded-4xl" />
                </TableCell>
                <TableCell className="tw:text-right">
                  <Skeleton className="tw:ml-auto tw:size-9 tw:rounded-md" />
                </TableCell>
              </TableRow>
            ))
          ) : error ? (
            <TableRow>
              <TableCell colSpan={3} className="tw:text-destructive">
                {i18n.t('Events:errorLoadingDeliveryAddresses', {
                  message: error.message,
                })}
              </TableCell>
            </TableRow>
          ) : deliveryAddresses.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="tw:text-muted-foreground">
                {i18n.t('Events:noDeliveryAddressesFound')}
              </TableCell>
            </TableRow>
          ) : (
            deliveryAddresses.map((address) => (
              <TableRow key={address.id}>
                <TableCell>
                  {[
                    [address.streetname, address.streetno]
                      .filter(Boolean)
                      .join(' '),
                    address.additional_info,
                  ]
                    .filter(Boolean)
                    .join(', ') || '-'}
                </TableCell>
                <TableCell>
                  <Badge variant="secondary" className="tw:min-w-5">
                    {address.linked_item_location_count}
                  </Badge>
                </TableCell>
                <TableCell className="tw:text-right">
                  {address.is_event_default ? (
                    <span className="tw:text-muted-foreground">
                      {i18n.t('Events:defaultDeliveryAddressLabel')}
                    </span>
                  ) : (
                    <div className="tw:flex tw:items-center tw:justify-end tw:gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void runPromote(address.id)}
                        disabled={
                          promotingAddress ||
                          deletingAddress ||
                          deletingAddressId === address.id ||
                          creatingAddress
                        }
                      >
                        {promotingAddressId === address.id && <Spinner />}
                        {i18n.t('Events:makeDefaultDeliveryAddress')}
                      </Button>
                      <Button
                        variant="secondary"
                        size="icon"
                        onClick={() => handleDeleteClick(address)}
                        disabled={
                          deletingAddress ||
                          deletingAddressId === address.id ||
                          creatingAddress ||
                          promotingAddress
                        }
                        aria-label={i18n.t(
                          'Events:deleteDeliveryAddressAriaLabel',
                          {
                            name: address.name || 'Unknown delivery address',
                          },
                        )}
                      >
                        {deletingAddressId === address.id ? (
                          <Spinner />
                        ) : (
                          <Trash2 className="tw:size-4" />
                        )}
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <form onSubmit={handleCreateAddress} className="tw:space-y-4">
            <DialogHeader>
              <DialogTitle>
                {i18n.t('Events:addAdditionalDeliveryAddress')}
              </DialogTitle>
              <DialogDescription>
                {i18n.t('Events:addAdditionalDeliveryAddressDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="tw:flex tw:gap-4">
              <div className="tw:grid tw:flex-1 tw:gap-2">
                <Label>{i18n.t('Events:streetName')}</Label>
                <Input
                  value={newStreetname}
                  onChange={(event) => setNewStreetname(event.target.value)}
                  autoFocus
                />
              </div>
              <div className="tw:grid tw:w-32 tw:gap-2">
                <Label>{i18n.t('Events:houseNumber')}</Label>
                <Input
                  value={newStreetno}
                  onChange={(event) => setNewStreetno(event.target.value)}
                />
              </div>
            </div>
            <div className="tw:grid tw:gap-2">
              <Label>{i18n.t('Events:additionalAddressInfo')}</Label>
              <Input
                value={newAdditionalInfo}
                onChange={(event) => setNewAdditionalInfo(event.target.value)}
              />
            </div>
            {deliveryAddresses.length === 0 ? (
              <p className="tw:text-muted-foreground tw:text-sm">
                {i18n.t('Events:firstAddressBecomesDefaultHint')}
              </p>
            ) : (
              <div className="tw:flex tw:items-center tw:gap-2">
                <Checkbox
                  id="delivery-address-is-event-default"
                  checked={newIsEventDefault}
                  onCheckedChange={(checked) =>
                    setNewIsEventDefault(checked === true)
                  }
                />
                <Label
                  htmlFor="delivery-address-is-event-default"
                  className="tw:font-normal"
                >
                  {i18n.t('Events:setAsDefaultDeliveryAddress')}
                </Label>
              </div>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsAddDialogOpen(false)}
                disabled={creatingAddress}
              >
                {i18n.t('Common:cancel')}
              </Button>
              <Button
                type="submit"
                disabled={!canSubmitAddress || creatingAddress}
              >
                {creatingAddress && <Spinner />}
                {i18n.t('Events:saveAddress')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(deleteConfirmAddress)}
        onOpenChange={(open) => {
          if (!open) setDeleteConfirmAddress(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{i18n.t('Events:deleteThisAddress')}</DialogTitle>
            <DialogDescription>
              {deleteConfirmAddress
                ? i18n.t(
                    deleteConfirmAddress.linked_item_location_count === 1
                      ? 'Events:deleteAddressLinkedDescriptionSingle'
                      : 'Events:deleteAddressLinkedDescriptionMultiple',
                    {
                      count: deleteConfirmAddress.linked_item_location_count,
                    },
                  )
                : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmAddress(null)}
              disabled={deletingAddress || creatingAddress}
            >
              {i18n.t('Common:cancel')}
            </Button>
            <Button
              onClick={() =>
                deleteConfirmAddress && void runDelete(deleteConfirmAddress.id)
              }
              disabled={deletingAddress || creatingAddress}
              variant="destructive"
            >
              {deletingAddress && <Spinner />}
              {i18n.t('Events:deleteAddress')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
